import React, { useRef, useMemo, useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import * as Styled from './ITeroOrderAdditionalInfo.styles';
import { formatDateToYYYYMMDD } from 'utils';
import { useI18n } from 'contexts/i18n';
import getConfig from 'next/config';
import { DateFieldWithPickerProps } from './ITeroOrderAdditionalInfo.types';
import { Radio } from 'components/core';

const DateFieldWithPicker = ({
  deliveryDate,
  handleChange,
}: DateFieldWithPickerProps) => {
  const i18n = useI18n();
  const { publicRuntimeConfig } = getConfig();
  const wrapperRef = useRef<HTMLLabelElement>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const asapSelected = !deliveryDate;

  const { minDate, maxDate } = useMemo(() => {
    const today = new Date();
    const min = new Date(today);
    min.setDate(
      today.getDate() +
        Number(publicRuntimeConfig.EU_ITERO_EARLIEST_DELIVERY_MIN_DAYS ?? 14)
    );

    const max = new Date(today);
    max.setDate(
      today.getDate() +
        Number(publicRuntimeConfig.EU_REQUESTED_DELIVERY_MAX_DAYS ?? 180)
    );

    return { minDate: min, maxDate: max };
  }, [publicRuntimeConfig]);

  const deliveryDateAsDate = useMemo(
    () => (deliveryDate ? new Date(deliveryDate) : null),
    [deliveryDate]
  );
  const displayDate = deliveryDate || formatDateToYYYYMMDD(minDate);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsCalendarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectDateHandler = (date: Date | null) => {
    if (date) {
      handleChange({
        target: { name: 'deliveryDate', value: formatDateToYYYYMMDD(date) },
      });
    }
    setIsCalendarOpen(false);
  };

  const handleAsapChange = () => {
    handleChange({ target: { name: 'deliveryDate', value: '' } });
  };

  const handleCustomDateClick = () => {
    setIsCalendarOpen(true);
    if (!deliveryDate) {
      handleChange({
        target: { name: 'deliveryDate', value: formatDateToYYYYMMDD(minDate) },
      });
    }
  };

  return (
    <>
      <Styled.Title>{i18n['euIteroShipmentPreferencesTitle']}</Styled.Title>
      <Styled.TextInputWrapper>
        <p>
          <em>
            {i18n['euIteroShipmentPreferencesDescription']}
            <br />
            {i18n['euIteroShipmentPreferencesDelayNote']}
          </em>
        </p>
      </Styled.TextInputWrapper>

      <Styled.CheckboxGroup>
        <Styled.TextInputWrapper>
          <Radio
            label={i18n['euIteroShipAsSoonAsPossibleLabel']}
            checked={asapSelected}
            onChange={handleAsapChange}
          />
        </Styled.TextInputWrapper>

        <Styled.TextInputWrapper>
          <Radio
            label={i18n['euIteroDoNotDeliverBeforeLabel']}
            checked={!asapSelected}
            onChange={handleCustomDateClick}
          />

          {!asapSelected && (
            <Styled.DateInputContainer ref={wrapperRef}>
              <Styled.DateDisplayInput
                type="text"
                readOnly
                placeholder="Select a date"
                value={displayDate}
                onClick={handleCustomDateClick}
              />
              {isCalendarOpen && (
                <Styled.CalendarContainer>
                  <DatePicker
                    selected={deliveryDateAsDate || minDate}
                    onChange={selectDateHandler}
                    minDate={minDate}
                    maxDate={maxDate}
                    inline
                  />
                </Styled.CalendarContainer>
              )}
            </Styled.DateInputContainer>
          )}
        </Styled.TextInputWrapper>
      </Styled.CheckboxGroup>
    </>
  );
};

export default DateFieldWithPicker;
